import { useState } from 'react'

import { env } from 'src/config/env'
import { paths } from 'src/config/paths'
import { useSignOut } from 'src/shared/features/auth/api/use-signout'
import useBreakpoint from 'src/shared/hooks/use-breakpoint'
import { useWindowSize } from 'src/shared/hooks/use-window-size'
import { cn } from 'src/shared/utils/cn'
import { ScrollBarTailwindClassesEnterprise } from 'src/shared/utils/tailwind-styles'

import { useAuthState } from '../../auth/api/use-auth-state'
import AuthHeaderLogo from '../../auth/components/auth-header-logo'
import { AuthLayoutContext } from '../../auth/contexts/auth-layout-context'
import SupportDialogV2 from '../../support/components/support-dialog-v2'

import EnterpriseAuthSideContent from './side-content'

interface EnterpriseAuthLayoutProps {
  children: React.ReactNode
  variant?: 'default' | 'consumer'
}

export default function EnterpriseAuthLayout({ children, variant = 'default' }: EnterpriseAuthLayoutProps) {
  const { isMobile } = useBreakpoint()
  const { width } = useWindowSize()
  const { isSignedIn, isConsumerSignedIn } = useAuthState()
  const [supportDialogOpen, setSupportDialogOpen] = useState(false)
  const signOut = useSignOut({ isEnterprise: true })
  const [showHeaderLogo, setShowHeaderLogo] = useState(false)
  const [headerLogoLinkTo, setHeaderLogoLinkTo] = useState(paths.root.getHref())

  const isLoggedIn = isSignedIn || isConsumerSignedIn || false

  const handleHelpClick = () => {
    setSupportDialogOpen(true)
  }

  const handlePrivacyPolicyClick = () => {
    window.open(env.LINK_TO_PRIVACY_POLICY, '_blank')
  }

  const resetContextValues = () => {
    setShowHeaderLogo(false)
    setHeaderLogoLinkTo(paths.root.getHref())
  }

  if (isMobile) {
    // Mobile version
    return (
      <AuthLayoutContext.Provider value={{ setShowHeaderLogo, setHeaderLogoLinkTo, reset: resetContextValues }}>
        <div className="flex min-h-screen w-full bg-white px-[25px]">{children}</div>
      </AuthLayoutContext.Provider>
    )
  }

  return (
    // Desktop version
    <AuthLayoutContext.Provider value={{ setShowHeaderLogo, setHeaderLogoLinkTo, reset: resetContextValues }}>
      <div
        className={cn(
          'relative flex h-screen w-full items-center justify-center overflow-y-auto bg-accent px-[25px] xs:h-auto xs:px-8 xs:py-16 lg:min-h-screen',
          variant === 'default' && ScrollBarTailwindClassesEnterprise,
        )}
      >
        {/* Header logo */}
        {showHeaderLogo && <AuthHeaderLogo headerLogoLinkTo={headerLogoLinkTo} />}

        <div
          className={cn(
            'mx-auto flex min-h-[750px] w-full max-w-[1330px] flex-row gap-[30px]',
            width < 1000 && 'mt-2 min-h-[1500px] flex-col',
            width < 1000 && variant === 'consumer' && 'mb-5 mt-0',
          )}
        >
          {children}
          <EnterpriseAuthSideContent variant={variant} />
        </div>
        <div className="absolute inset-x-0 bottom-0 flex flex-row items-center justify-end gap-10 px-[43px] py-[34px] text-[13px] font-medium leading-[1.6] text-muted">
          <button type="button" className="cursor-pointer" onClick={handleHelpClick}>
            Need help
          </button>
          <button type="button" className="cursor-pointer" onClick={handlePrivacyPolicyClick}>
            Privacy policy
          </button>
          {variant === 'default' && isLoggedIn && (
            <button type="button" className="cursor-pointer" onClick={() => signOut.mutateAsync()}>
              Logout
            </button>
          )}
        </div>
        <SupportDialogV2 open={supportDialogOpen} setOpen={setSupportDialogOpen} />
      </div>
    </AuthLayoutContext.Provider>
  )
}
